/**
 * Formatting for the stock Dividend tab. Every formatter returns null for a missing input and the
 * page renders nothing for null: a dash or a zero would read as a value.
 */
import { DivCommentary, DivEventRow, DivOutcome, DivStockResponse } from "@/models/dividends-v2";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const isNum = (v: number | null | undefined): v is number => typeof v === "number" && Number.isFinite(v);

const trimZero = (s: string) => s.replace(/\.0$/, "");

const thisYear = (): number => new Date().getFullYear();

/** "2026-11-09" -> "9 Nov" this year, "9 Nov 2027" in any other year. */
export const fmtDate = (iso: string | null | undefined, alwaysYear = false): string | null => {
    if (!iso) return null;
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return null;
    return `${d} ${MONTHS[m - 1]}${alwaysYear || y !== thisYear() ? ` ${y}` : ""}`;
};

/** "2026-11-09" -> "9 Nov 26", the ladder's compact form. */
export const fmtDateShort = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return null;
    return `${d} ${MONTHS[m - 1]} ${String(y).slice(2)}`;
};

/** "2026-05-01" -> "May 26". */
export const fmtMonth = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const [y, m] = iso.slice(0, 10).split("-").map(Number);
    if (!y || !m) return null;
    return `${MONTHS[m - 1]} ${String(y).slice(2)}`;
};

/** A per-share amount: at least two decimals, up to four when the amount has them ("0.26", "0.125", "0.0125"). */
export const fmtAmount = (v: number | null | undefined): string | null => {
    if (!isNum(v)) return null;
    return v.toFixed(4).replace(/(\.\d\d\d*?)0+$/, "$1");
};

/**
 * A per-share amount with its currency, so it never reads as a yield: "$0.27", "€1.20", "0.05 HKD"
 * where the currency has no distinct symbol. Two to four decimals, as {@link fmtAmount}.
 */
export const fmtMoney = (v: number | null | undefined, currency: string | null | undefined): string | null => {
    const plain = fmtAmount(v);
    if (plain === null || !isNum(v)) return null;
    const code = (currency ?? "").toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) return plain;
    try {
        const s = new Intl.NumberFormat("en-GB", { style: "currency", currency: code, currencyDisplay: "narrowSymbol", minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(v);
        // A currency with no symbol of its own comes back as its code: put the code after the number.
        return s.includes(code) ? `${plain} ${code}` : s;
    } catch {
        return `${plain} ${code}`;
    }
};

/** A per-cent field (0.31 = 0.31%): "0.31%", one decimal from 10. */
export const fmtPct = (v: number | null | undefined, digits?: number): string | null => {
    if (!isNum(v)) return null;
    const d = digits ?? (Math.abs(v) >= 10 ? 0 : Math.abs(v) >= 1 ? 1 : 2);
    return `${trimZero(v.toFixed(d))}%`;
};

export const fmtPctSigned = (v: number | null | undefined, digits = 1): string | null => {
    if (!isNum(v)) return null;
    const s = trimZero(Math.abs(v).toFixed(digits));
    return `${v > 0 ? "+" : v < 0 ? "−" : ""}${s}%`;
};

/** A probability 0..1: "91%", "<1%" for a small non-zero chance. */
export const fmtProb = (p: number | null | undefined): string | null => {
    if (!isNum(p)) return null;
    if (p > 0 && p < 0.005) return "<1%";
    return `${Math.round(p * 100)}%`;
};

export const OUTCOME_LABEL: Record<DivOutcome, string> = { raise: "Raise", hold: "Hold", cut: "Cut", omit: "Omit", special: "Special" };

export const outcomeLabel = (o: DivOutcome | null | undefined): string | null => (o ? OUTCOME_LABEL[o] ?? o : null);

/** "quarterly" -> "quarterly"; the enum values read as words already. */
export const frequencyLabel = (f: string | null | undefined): string | null => (f ? f.replace(/_/g, " ").toLowerCase() : null);

/** "+/- 1 day" band of a forecast date; nothing once declared. */
export const fmtBand = (days: number | null | undefined): string | null => (isNum(days) && days > 0 ? `±${days} ${days === 1 ? "day" : "days"}` : null);

export const fmtBandShort = (days: number | null | undefined): string | null => (isNum(days) && days > 0 ? `±${days}d` : null);

/** Calendar days between two ISO dates (b - a). */
export const daysBetween = (a: string, b: string): number => Math.round((Date.parse(`${b.slice(0, 10)}T00:00:00Z`) - Date.parse(`${a.slice(0, 10)}T00:00:00Z`)) / 86_400_000);

/** The ladder's date delta for a past row: "date exact", "date +1d", or null without a forecast date. */
export const dateDelta = (forecastIso: string | null | undefined, actualIso: string | null | undefined): string | null => {
    if (!forecastIso || !actualIso) return null;
    const d = daysBetween(forecastIso, actualIso);
    return d === 0 ? "date exact" : `date ${d > 0 ? "+" : "−"}${Math.abs(d)}d`;
};

/** Chance of a cut or an omission on one event. */
export const cutChance = (row: Pick<DivEventRow, "p_cut" | "p_omit"> | null | undefined): number | null => (row && (isNum(row.p_cut) || isNum(row.p_omit)) ? (row.p_cut ?? 0) + (row.p_omit ?? 0) : null);

const sentence = (parts: (string | null | false | undefined)[]): string => parts.filter(Boolean).join(" ");

/**
 * The commentary box's paragraphs. TED builds them in code from the payload (`commentary`); when a
 * payload comes without them the page builds the same three short paragraphs here, from the same
 * figures (timing, coverage, what the market says). Never invents a figure the payload lacks.
 */
export const buildCommentary = (p: DivStockResponse): DivCommentary[] => {
    if (p.commentary && p.commentary.length) return p.commentary;
    // Before the forecasts are published TED writes the words from what is left; nothing is guessed here.
    if (p.published === false) return [];
    const out: DivCommentary[] = [];
    const n = p.next;
    const name = p.company_name || p.ticker;
    if (n) {
        const ccy = n.currency ?? p.currency;
        const amount = fmtMoney(n.amount_p50, ccy);
        const decl = fmtDate(n.declaration_date);
        const ex = fmtDate(n.ex_date);
        const late = n.status === "late";
        out.push({
            title: "Timing",
            text: sentence([
                late
                    ? `${name}'s ${n.period_label} dividend is ${n.days_late ? `${n.days_late} ${n.days_late === 1 ? "day" : "days"} ` : ""}late against its usual timing.`
                    : n.status === "declared"
                    ? `${name} has declared ${fmtMoney(n.declared_amount, ccy) ?? "its next dividend"}${ex ? `, going ex on ${ex}` : ""}.`
                    : `${name} is expected to declare${amount ? ` ${amount}` : ""}${decl ? ` on ${decl}` : ""}${ex ? ` and go ex on ${ex}` : ""}.`,
                p.pattern ? `Pattern: ${p.pattern.charAt(0).toLowerCase()}${p.pattern.slice(1)}.` : null,
            ]),
        });
    }
    const c = p.coverage;
    if (c && (isNum(c.payout_earnings_fwd_pct) || isNum(c.payout_fcf_pct))) {
        const stretched = (c.payout_fcf_pct ?? 0) > 100 || (c.payout_earnings_fwd_pct ?? 0) > 100;
        out.push({
            title: "Coverage",
            text: sentence([
                stretched ? "The payout is not covered." : "The payout is covered.",
                isNum(c.payout_earnings_fwd_pct) && `The dividend is ${fmtPct(c.payout_earnings_fwd_pct, 0)} of expected earnings`,
                isNum(c.payout_fcf_pct) && `${isNum(c.payout_earnings_fwd_pct) ? "and" : "The dividend is"} ${fmtPct(c.payout_fcf_pct, 0)} of trailing free cash flow.`,
                isNum(c.net_cash_per_share) && (c.net_cash_per_share >= 0 ? `Net cash is ${fmtAmount(c.net_cash_per_share)} per share.` : `Net debt is ${fmtAmount(-c.net_cash_per_share)} per share.`),
            ]),
        });
    }
    const opt = n && isNum(n.options_implied) ? n.options_implied : null;
    const cut = cutChance(n);
    if (n && (opt !== null || isNum(cut) || p.borrow)) {
        out.push({
            title: "What the market says",
            text: sentence([
                opt !== null && `Options imply ${fmtMoney(opt, n.currency ?? p.currency)} for the next dividend${isNum(n.amount_p50) ? ` against a forecast of ${fmtMoney(n.amount_p50, n.currency ?? p.currency)}` : ""}.`,
                isNum(cut) && cut >= 0.25 && `The chance of a cut or omission is ${fmtProb(cut)}.`,
                p.borrow &&
                    isNum(p.borrow.short_interest_pct_ff) &&
                    `Short interest is ${fmtPct(p.borrow.short_interest_pct_ff)} of free float${p.borrow.record_spike ? ", with a borrowing spike around the last record date" : ", with no borrowing spike around the last record date"}.`,
            ]),
        });
    }
    return out.filter((x) => x.text);
};
